import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Armchair, ArrowRight } from 'lucide-react';
export function HeroContent() {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    who: false,
    what: false,
    behind: false
  });
  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    ease: 'easeOut'
  }} className="relative w-full max-w-4xl mx-auto p-2 md:p-4 mt-[10vh] md:mt-0" data-id="element-71">
      {/* Fonts import */}
      <style data-id="element-72">{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&display=swap');
      `}</style>

      {/* Cloud decoration at screen edge - fixed position */}
      <img src="https://cdn.magicpatterns.com/uploads/d7rZimoLM3B5uNDaDGfmTC/cloud.png" alt="" className="hidden md:block fixed right-0 w-[38rem] h-auto pointer-events-none" style={{
      top: '10vh',
      zIndex: 15,
      opacity: 0.4
    }} data-id="element-73" />

      {/* Main Container */}
      <div className="relative p-8 md:p-16 min-h-[80vh] flex flex-col rounded-2xl shadow-2xl bg-white" data-id="element-74">
        {/* Content */}
        <div className="w-full z-10 flex flex-col" data-id="element-75">
          {/* 1. Main Introduction */}
          <div className="mb-10" data-id="element-76">
            <p className="text-xs uppercase tracking-wider mb-3 text-left font-semibold" style={{
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(45, 74, 79, 0.6)'
          }} data-id="element-77">
              sponsored by Nomad Citizen
            </p>

            <h1 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-left" style={{
            fontFamily: "'Playfair Display', serif",
            color: '#2D4A4F'
          }} data-id="element-78">
              Post-Strangers Explained
            </h1>

            <p className="text-base font-bold leading-relaxed max-w-2xl text-left mb-4" style={{
            fontFamily: "'Inter', sans-serif",
            color: '#2D4A4F'
          }} data-id="element-79">
              Post-strangers is a free dinner series for select nomads.
            </p>

            <p className="text-base leading-relaxed max-w-2xl text-left mb-4" style={{
            fontFamily: "'Inter', sans-serif",
            color: '#2D4A4F'
          }} data-id="element-80">
              We host intimate dinners in cities around the world - Lisbon,
              Chiang Mai, Amsterdam, Poland, and wherever our community lands
              next.
            </p>

            <p className="text-base leading-relaxed max-w-2xl text-left hidden md:block" style={{
            fontFamily: "'Inter', sans-serif",
            color: '#2D4A4F'
          }} data-id="element-81">
              Each dinner seats 8-12 people for a meal and a guided conversation
              about the things we don't usually talk about: what nomadism means
              to you, what we're building toward, and the invisible structures
              that hold our lives together.
            </p>
          </div>

          {/* Mobile CTA (visible only on small screens) - Moved here for better conversion */}
          <button className="mb-10 lg:hidden w-full py-4 bg-[#d4e20f] border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] font-bold text-sm uppercase tracking-wider active:translate-y-1 active:shadow-none transition-all" data-id="element-82">
            Apply Now
          </button>

          {/* 2. Upcoming Dinners Section */}
          <div className="mb-12" data-id="element-83">
            <div className="flex items-center justify-between mb-6" data-id="element-84">
              <h2 className="text-2xl font-bold tracking-tight text-left" style={{
              fontFamily: "'Playfair Display', serif",
              color: '#2D4A4F'
            }} data-id="element-85">
                Upcoming Dinners
              </h2>

              <a href="/dinners" className="flex items-center gap-2 text-sm font-medium hover:underline whitespace-nowrap" style={{
              color: '#2D4A4F'
            }} data-id="element-86">
                View all dinners
                <ArrowRight size={16} data-id="element-87" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-88">
              {/* Chiang Mai Dinner */}
              <div className="border-2 border-black rounded-lg overflow-hidden bg-white/80" data-id="element-89">
                <div className="h-40 overflow-hidden" data-id="element-90">
                  <img src="https://cdn.magicpatterns.com/uploads/3X9TLZUDybLFDUXCx3mGdv/Screenshot_2026-01-20_at_5.04.57_PM.png" alt="Chiang Mai dinner" className="w-full h-full object-cover" style={{
                  transform: 'scale(1.05)',
                  objectPosition: 'center 20%'
                }} data-id="element-91" />
                </div>
                <div className="p-5" data-id="element-92">
                  <div className="flex justify-between items-start mb-3" data-id="element-93">
                    <div className="flex-1 pr-2" data-id="element-94">
                      <h3 className="font-bold text-lg leading-tight" style={{
                      fontFamily: "'Playfair Display', serif",
                      fontStyle: 'italic',
                      fontWeight: 700,
                      color: '#2D4A4F'
                    }} data-id="element-95">
                        The Invisible Structures That Shape Our Lives
                      </h3>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-2" data-id="element-96">
                        Chiang Mai, Thailand
                      </p>
                    </div>
                    <span className="bg-[#E5D68A] px-2 py-1 text-xs font-bold border border-black rounded-full whitespace-nowrap" data-id="element-97">
                      3 FEB 2026
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 mb-4" data-id="element-98">
                    Join us for an intimate evening exploring the hidden
                    frameworks that guide our choices, relationships, and sense
                    of belonging.
                  </p>
                  <a href="https://www.altcoliving.com/food-that-nourishes-us-residency/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs font-bold uppercase tracking-wider underline hover:text-orange-500" data-id="element-99">
                    <Armchair size={14} className="mr-1.5" data-id="element-100" />
                    Request a seat
                  </a>
                </div>
              </div>

              {/* Hong Kong Dinner */}
              <div className="border-2 border-black rounded-lg overflow-hidden bg-white/80" data-id="element-101">
                <div className="h-40 overflow-hidden" data-id="element-102">
                  <img src="https://cdn.magicpatterns.com/uploads/twXB3kmZmS464MLKc9NuRP/0_2-2.png" alt="Hong Kong dinner" className="w-full h-full object-cover" style={{
                  transform: 'scale(1.05)',
                  objectPosition: 'center 60%'
                }} data-id="element-103" />
                </div>
                <div className="p-5" data-id="element-104">
                  <div className="flex justify-between items-start mb-3" data-id="element-105">
                    <div className="flex-1 pr-2" data-id="element-106">
                      <h3 className="font-bold text-lg leading-tight" style={{
                      fontFamily: "'Playfair Display', serif",
                      fontStyle: 'italic',
                      fontWeight: 700,
                      color: '#2D4A4F'
                    }} data-id="element-107">
                        The Archipelago Life
                      </h3>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mt-2" data-id="element-108">
                        Hong Kong
                      </p>
                    </div>
                    <span className="bg-[#E5D68A] px-2 py-1 text-xs font-bold border border-black rounded-full whitespace-nowrap" data-id="element-109">
                      9 FEB 2026
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2 mb-4" data-id="element-110">
                    An evening celebrating the beauty of living between
                    worlds—island hopping through life, culture, and identity.
                  </p>
                  <a href="https://luma.com/viarx0j7" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs font-bold uppercase tracking-wider underline hover:text-orange-500" data-id="element-111">
                    <Armchair size={14} className="mr-1.5" data-id="element-112" />
                    Request a seat
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* 3. How It Works Section */}
          <div className="mb-12" data-id="element-113">
            <h2 className="text-2xl font-bold mb-6 tracking-tight text-left" style={{
            fontFamily: "'Playfair Display', serif",
            color: '#2D4A4F'
          }} data-id="element-114">
              How it works
            </h2>

            <p className="text-base leading-relaxed max-w-2xl text-left mb-8" style={{
            fontFamily: "'Inter', sans-serif",
            color: '#2D4A4F'
          }} data-id="element-115">
              We take dinners seriously, so we're choosy - we'll ask you a few
              questions to make sure it's a good fit.
            </p>

            <div className="space-y-8" data-id="element-116">
              {/* Step 1 */}
              <div className="flex gap-6 items-start" data-id="element-117">
                <div className="flex-shrink-0 w-20 h-20 bg-gray-50 rounded-full overflow-hidden border border-gray-200" data-id="element-118">
                  <img src="https://cdn.magicpatterns.com/uploads/s8w5r8RPRV3TK6ExAhFh8m/SafetyWing-Dare-to-make-mistakes.gif" alt="Step 1" className="w-full h-full object-cover" data-id="element-119" />
                </div>
                <div className="flex-1 pt-1" data-id="element-120">
                  <h3 className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                  fontFamily: "'Inter', sans-serif",
                  color: '#2D4A4F'
                }} data-id="element-121">
                    Step 1 • Look at Events
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700" style={{
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-122">
                    Look at which events you want to join on our calendar.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6 items-start" data-id="element-123">
                <div className="flex-shrink-0 w-20 h-20 bg-gray-50 rounded-full overflow-hidden border border-gray-200" data-id="element-124">
                  <img src="https://cdn.magicpatterns.com/uploads/5TXtLeSVRZsDJ25dhcUBfi/SafetyWing-Social-safety-net.gif" alt="Step 2" className="w-full h-full object-cover" data-id="element-125" />
                </div>
                <div className="flex-1 pt-1" data-id="element-126">
                  <h3 className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                  fontFamily: "'Inter', sans-serif",
                  color: '#2D4A4F'
                }} data-id="element-127">
                    Step 2 • Tell Us About You
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700" style={{
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-128">
                    Tell us a bit about yourself and why this dinner calls to
                    you.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6 items-start" data-id="element-129">
                <div className="flex-shrink-0 w-20 h-20 bg-gray-50 rounded-full overflow-hidden border border-gray-200" data-id="element-130">
                  <img src="https://cdn.magicpatterns.com/uploads/o35zvnaANB45VNYst6DS5u/SafetyWing-Do-the-right-thing.gif" alt="Step 3" className="w-full h-full object-cover" data-id="element-131" />
                </div>
                <div className="flex-1 pt-1" data-id="element-132">
                  <h3 className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                  fontFamily: "'Inter', sans-serif",
                  color: '#2D4A4F'
                }} data-id="element-133">
                    Step 3 • Get Approved
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700" style={{
                  fontFamily: "'Inter', sans-serif"
                }} data-id="element-134">
                    Our team will review your application and send you a
                    confirmation message once you are approved!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 4. FAQ Accordion Section - Editorial Clean Design */}
          <div className="mt-4 border-t border-gray-200" data-id="element-135">
            {/* Who is this for */}
            <div className="border-b border-gray-200" data-id="element-136">
              <button onClick={() => toggleSection('who')} className="w-full py-5 flex items-start justify-between group" data-id="element-137">
                <h2 className="text-lg font-bold text-left pr-8 group-hover:text-gray-600 transition-colors" style={{
                fontFamily: "'Playfair Display', serif",
                color: '#2D4A4F'
              }} data-id="element-138">
                  Who is this for?
                </h2>
                <motion.div animate={{
                rotate: openSections.who ? 45 : 0
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="flex-shrink-0 mt-1" data-id="element-139">
                  <Plus size={20} strokeWidth={1.5} color="#2D4A4F" data-id="element-140" />
                </motion.div>
              </button>
              <AnimatePresence data-id="element-141">
                {openSections.who && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }} className="overflow-hidden" data-id="element-142">
                    <div className="pb-6 pr-12" data-id="element-143">
                      <p className="text-sm text-gray-700 leading-relaxed" style={{
                    fontFamily: "'Inter', sans-serif"
                  }} data-id="element-144">
                        Remote workers, founders, freelancers, nomads, and
                        anyone whose community is scattered across time zones.
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* What happens at a dinner */}
            <div className="border-b border-gray-200" data-id="element-145">
              <button onClick={() => toggleSection('what')} className="w-full py-5 flex items-start justify-between group" data-id="element-146">
                <h2 className="text-lg font-bold text-left pr-8 group-hover:text-gray-600 transition-colors" style={{
                fontFamily: "'Playfair Display', serif",
                color: '#2D4A4F'
              }} data-id="element-147">
                  What happens at a dinner?
                </h2>
                <motion.div animate={{
                rotate: openSections.what ? 45 : 0
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="flex-shrink-0 mt-1" data-id="element-148">
                  <Plus size={20} strokeWidth={1.5} color="#2D4A4F" data-id="element-149" />
                </motion.div>
              </button>
              <AnimatePresence data-id="element-150">
                {openSections.what && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }} className="overflow-hidden" data-id="element-151">
                    <div className="pb-6 pr-12" data-id="element-152">
                      <p className="text-sm text-gray-700 leading-relaxed" style={{
                    fontFamily: "'Inter', sans-serif"
                  }} data-id="element-153">
                        You'll share a meal with a small group of people you've
                        never met. There's a theme, a few questions to spark
                        conversation, and plenty of room for it to go wherever
                        it goes.
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Who's behind this */}
            <div className="border-b border-gray-200" data-id="element-154">
              <button onClick={() => toggleSection('behind')} className="w-full py-5 flex items-start justify-between group" data-id="element-155">
                <h2 className="text-lg font-bold text-left pr-8 group-hover:text-gray-600 transition-colors" style={{
                fontFamily: "'Playfair Display', serif",
                color: '#2D4A4F'
              }} data-id="element-156">
                  Who's behind this?
                </h2>
                <motion.div animate={{
                rotate: openSections.behind ? 45 : 0
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="flex-shrink-0 mt-1" data-id="element-157">
                  <Plus size={20} strokeWidth={1.5} color="#2D4A4F" data-id="element-158" />
                </motion.div>
              </button>
              <AnimatePresence data-id="element-159">
                {openSections.behind && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }} className="overflow-hidden" data-id="element-160">
                    <div className="pb-6 pr-12" data-id="element-161">
                      <p className="text-sm text-gray-700 leading-relaxed" style={{
                    fontFamily: "'Inter', sans-serif"
                  }} data-id="element-162">
                        Post-strangers is hosted by Nomad Citizen, a membership
                        that provides the social infrastructure for location
                        independent lives, these dinners are one part of what
                        we're building.
                      </p>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}